import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Folder,
  File as FileIcon,
  Download,
  Upload,
  RefreshCw,
  Home,
  ChevronRight,
  FileText,
  Image,
  Table,
  Presentation,
  CheckCircle,
  Loader2,
  Bell,
  BellOff,
  Radio,
  Rocket
} from 'lucide-react';
import { useSharePoint } from '../hooks/useSharePoint';
import { useSharePointSSE, FileChangeEvent } from '../hooks/useSharePointSSE';
import sharepointSyncService, { DriveItem, Breadcrumb as SharePointBreadcrumb } from '../services/sharepoint-sync.service';
import sharepointWebhookService from '../services/sharepoint-webhook.service';
import { pdfAnalysisService } from '../services/pdf-analysis.service';
import { UploadPdfModal } from '../components/UploadPdfModal';
import { PageHeader } from '../components/PageHeader';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/api.config';

const SharePointSync: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRAGMode = searchParams.get('mode') === 'rag';
  const {
    sites,
    libraries,
    loading,
    error,
    fetchSites,
    fetchLibraries,
    clearError,
  } = useSharePoint();

  const [selectedSite, setSelectedSite] = useState<string>('');
  const [selectedLibrary, setSelectedLibrary] = useState<string>('');
  const [items, setItems] = useState<DriveItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<SharePointBreadcrumb[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // RAG status tracking
  const [filesInRAG, setFilesInRAG] = useState<Record<string, boolean>>({});
  const [uploadingToPinecone, setUploadingToPinecone] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Legacy states (keeping for compatibility)
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingItemId, setDownloadingItemId] = useState<string | null>(null);
  const [uploadingItemId, setUploadingItemId] = useState<string | null>(null);

  // Slides generation state
  const [generatingSlidesItemId, setGeneratingSlidesItemId] = useState<string | null>(null);

  // Real-time updates state
  const [webhookSubscribed, setWebhookSubscribed] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);

  // SSE Connection
  const { isConnected: sseConnected } = useSharePointSSE({
    driveId: realtimeEnabled ? selectedLibrary : undefined,
    onFileChange: handleFileChange,
    onConnected: () => {
      Swal.fire({
        icon: 'success',
        title: 'Real-time Updates Active',
        text: 'You will be notified when files change in SharePoint',
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    },
  });

  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    if (selectedLibrary) {
      checkWebhookSubscription();
    } else {
      setWebhookSubscribed(false);
      setSubscriptionId(null);
      setRealtimeEnabled(false);
    }
  }, [selectedLibrary]);

  const checkWebhookSubscription = async () => {
    try {
      const subscription = await sharepointWebhookService.getLibrarySubscription(selectedLibrary);
      if (subscription) {
        setWebhookSubscribed(true);
        setSubscriptionId(subscription.subscriptionId);
      } else {
        setWebhookSubscribed(false);
        setSubscriptionId(null);
      }
    } catch (error) {
      console.error('[SharePointSync] Error checking webhook subscription:', error);
    }
  };

  function handleFileChange(event: FileChangeEvent) {
    const changeIcons: Record<string, string> = {
      added: '➕',
      updated: '🔄',
      deleted: '🗑️',
      restored: '♻️',
    };

    const icon = changeIcons[event.changeType] || '📄';
    const fileName = event.fileDetails?.name || 'Unknown file';

    Swal.fire({
      icon: 'info',
      title: `${icon} File ${event.changeType}`,
      text: fileName,
      timer: 3000,
      showConfirmButton: false,
      toast: true,
      position: 'top-end',
    });

    if (selectedLibrary) {
      const currentFolderId = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.id : null;
      loadFolderContents(selectedLibrary, currentFolderId);
    }
  }

  const getFileIcon = (mimeType: string, fileName: string) => {
    if (mimeType.includes('pdf') || fileName.endsWith('.pdf'))
      return <FileText className="w-6 h-6 text-red-500" />;
    if (mimeType.includes('word') || fileName.endsWith('.docx') || fileName.endsWith('.doc'))
      return <FileText className="w-6 h-6 text-blue-500" />;
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet') || fileName.endsWith('.xlsx') || fileName.endsWith('.xls'))
      return <Table className="w-6 h-6 text-green-500" />;
    if (mimeType.includes('powerpoint') || fileName.endsWith('.pptx') || fileName.endsWith('.ppt'))
      return <Presentation className="w-6 h-6 text-orange-500" />;
    if (mimeType.includes('image'))
      return <Image className="w-6 h-6 text-purple-500" />;
    return <FileIcon className="w-6 h-6 text-gray-500" />;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleSiteChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const siteId = e.target.value;
    setSelectedSite(siteId);
    setSelectedLibrary('');
    setItems([]);
    setBreadcrumbs([]);
    if (siteId) {
      fetchLibraries(siteId);
    }
  };

  const handleLibraryChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const driveId = e.target.value;
    setSelectedLibrary(driveId);
    setBreadcrumbs([]);
    if (driveId) {
      await loadFolderContents(driveId, null);
    }
  };

  const loadFolderContents = async (driveId: string, folderId: string | null) => {
    setLoadingItems(true);
    try {
      const folderItems = await sharepointSyncService.getDriveItems(driveId, folderId || undefined);
      setItems(folderItems);

      const ragStatus: Record<string, boolean> = {};
      const fileItems = folderItems.filter(item => !item.folder);

      await Promise.all(
        fileItems.map(async (item) => {
          const inRAG = await sharepointSyncService.checkFileInRAG(item.id);
          ragStatus[item.id] = inRAG;
        })
      );

      setFilesInRAG(ragStatus);

      if (folderId) {
        const breadcrumbsData = await sharepointSyncService.getBreadcrumbs(driveId, folderId);
        setBreadcrumbs(breadcrumbsData);
      } else {
        setBreadcrumbs([]);
      }
    } catch (err) {
      console.error('Failed to load folder contents:', err);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleFolderClick = (folderId: string) => {
    if (selectedLibrary) {
      loadFolderContents(selectedLibrary, folderId);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      loadFolderContents(selectedLibrary, null);
    } else {
      const breadcrumb = breadcrumbs[index];
      if (breadcrumb.id) {
        loadFolderContents(selectedLibrary, breadcrumb.id);
      }
    }
  };

  const handleDownload = async (driveId: string, itemId: string, fileName: string) => {
    try {
      setDownloadingItemId(itemId);
      const blob = await sharepointSyncService.downloadFile(driveId, itemId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download file:', err);
    } finally {
      setDownloadingItemId(null);
    }
  };

  const handleUploadClick = async (driveId: string, itemId: string, fileName: string) => {
    try {
      setUploadError(null);
      setUploadingItemId(itemId);

      const blob = await sharepointSyncService.downloadFile(driveId, itemId);
      const file = new File([blob], fileName, { type: 'application/pdf' });

      setUploadingFile(file);
      setShowUploadModal(true);
    } catch (err) {
      console.error('Failed to prepare file for upload:', err);
      setUploadError('Failed to load file from SharePoint');
    } finally {
      setUploadingItemId(null);
    }
  };

  const handleUploadToAnalysis = async () => {
    if (!uploadingFile) return;

    try {
      setIsUploading(true);
      setUploadError(null);

      const result = await pdfAnalysisService.analyzePdf(uploadingFile, 'anbima');

      setShowUploadModal(false);
      setUploadingFile(null);

      navigate(`/pdf-analysis/${result._id}`);
    } catch (err: any) {
      console.error('Failed to upload file for analysis:', err);
      setUploadError(err.response?.data?.error || err.message || 'Failed to start analysis');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setUploadingFile(null);
    setUploadError(null);
  };

  const handleUploadToPinecone = async (driveId: string, itemId: string, fileName: string, mimeType: string, webUrl: string) => {
    try {
      setUploadError(null);
      setSuccessMessage(null);
      setUploadingToPinecone(itemId);

      if (filesInRAG[itemId]) {
        setUploadError(`⚠️ "${fileName}" is already saved in Pinecone`);
        setTimeout(() => setUploadError(null), 3000);
        setUploadingToPinecone(null);
        return;
      }

      await sharepointSyncService.saveToRAG(driveId, itemId, fileName, mimeType, webUrl);

      setFilesInRAG(prev => ({ ...prev, [itemId]: true }));

      setSuccessMessage(`✅ "${fileName}" successfully uploaded to Pinecone!`);
      setTimeout(() => setSuccessMessage(null), 5000);

      setTimeout(async () => {
        const stillExists = await sharepointSyncService.checkFileInRAG(itemId);
        if (stillExists) {
          setFilesInRAG(prev => ({ ...prev, [itemId]: true }));
        }
      }, 3000);

    } catch (err: any) {
      console.error('[SharePointSync] ❌ Upload failed:', err);

      if (err.alreadySaved) {
        setUploadError(`❌ "${fileName}" already exists in Pinecone database`);
        setFilesInRAG(prev => ({ ...prev, [itemId]: true }));
      } else {
        setUploadError(err.message || 'Failed to upload to Pinecone');
      }

      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploadingToPinecone(null);
    }
  };

  const handleToggleWebhook = async () => {
    if (!selectedSite || !selectedLibrary) {
      Swal.fire({
        icon: 'warning',
        title: 'No Library Selected',
        text: 'Please select a library first',
      });
      return;
    }

    try {
      if (webhookSubscribed && subscriptionId) {
        await sharepointWebhookService.unsubscribe(subscriptionId);
        setWebhookSubscribed(false);
        setSubscriptionId(null);
        setRealtimeEnabled(false);

        Swal.fire({
          icon: 'success',
          title: 'Unsubscribed',
          text: 'Real-time updates disabled',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        const selectedLib = libraries.find(lib => lib.id === selectedLibrary);
        const subscription = await sharepointWebhookService.subscribe(
          selectedSite,
          selectedLibrary,
          selectedLib?.name
        );

        setWebhookSubscribed(true);
        setSubscriptionId(subscription.subscriptionId);

        Swal.fire({
          icon: 'success',
          title: 'Subscribed!',
          text: 'Real-time updates enabled',
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error: any) {
      console.error('[SharePointSync] Error toggling webhook:', error);
      Swal.fire({
        icon: 'error',
        title: 'Subscription Failed',
        text: error.message || 'Failed to manage webhook subscription',
      });
    }
  };

  const handleToggleRealtime = () => {
    if (!webhookSubscribed) {
      Swal.fire({
        icon: 'warning',
        title: 'Webhook Not Subscribed',
        text: 'Please enable webhook subscription first',
      });
      return;
    }

    setRealtimeEnabled(!realtimeEnabled);
  };

  const handleGenerateSlides = async (driveId: string, itemId: string, fileName: string) => {
    try {
      if (!fileName.toLowerCase().endsWith('.docx')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File Type',
          text: 'Only DOCX files can be used to generate slides'
        });
        return;
      }

      const result = await Swal.fire({
        title: 'Generate Slides',
        html: `
          <p style="margin-bottom: 1rem;">Choose slides per page for <strong>${fileName}</strong></p>
          <div style="display: flex; gap: 1rem; justify-content: center;">
            <button id="slides-2" class="swal2-confirm swal2-styled" style="background-color: #0078d4;">
              2 Slides/Page
            </button>
            <button id="slides-4" class="swal2-confirm swal2-styled" style="background-color: #ea7500;">
              4 Slides/Page
            </button>
          </div>
        `,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        didOpen: () => {
          const slides2Btn = document.getElementById('slides-2');
          const slides4Btn = document.getElementById('slides-4');

          slides2Btn?.addEventListener('click', () => {
            Swal.clickConfirm();
            (Swal.getPopup() as any).slidesPerPage = 2;
          });

          slides4Btn?.addEventListener('click', () => {
            Swal.clickConfirm();
            (Swal.getPopup() as any).slidesPerPage = 4;
          });
        },
        preConfirm: () => {
          return (Swal.getPopup() as any).slidesPerPage;
        }
      });

      if (result.dismiss) return;

      const slidesPerPage = result.value;
      if (!slidesPerPage) return;

      Swal.fire({
        title: 'Generating Slides...',
        html: 'Please wait while we generate your PDF slides',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      setGeneratingSlidesItemId(itemId);

      const response = await fetch(`${API_BASE_URL}/slides/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileId: itemId,
          driveId: driveId,
          fileName: fileName,
          slidesPerPage: slidesPerPage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate slides');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName.replace('.docx', '')}_${slidesPerPage}_slides.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Slides generated successfully! (${slidesPerPage} slides per page)`,
        timer: 3000,
        showConfirmButton: false,
      });

    } catch (err: any) {
      console.error('Error generating slides:', err);
      Swal.fire({
        icon: 'error',
        title: 'Generation Failed',
        text: err.message || 'Failed to generate slides'
      });
    } finally {
      setGeneratingSlidesItemId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header with Breadcrumbs */}
      <PageHeader
        title="SharePoint Browser"
        description="Browse and download content from SharePoint"
        breadcrumbs={[{ label: 'SharePoint Sync' }]}
      />

      {/* Selectors Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Site Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select SharePoint Site
            </label>
            <select
              value={selectedSite}
              onChange={handleSiteChange}
              disabled={loading}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">-- Select a site --</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.displayName || site.name}
                </option>
              ))}
            </select>
          </div>

          {/* Library Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Document Library
            </label>
            <select
              value={selectedLibrary}
              onChange={handleLibraryChange}
              disabled={!selectedSite || loading}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            >
              <option value="">-- Select a library --</option>
              {libraries.map((lib) => (
                <option key={lib.id} value={lib.id}>
                  {lib.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Real-time Updates Controls */}
        {selectedLibrary && (
          <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleToggleWebhook}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                webhookSubscribed
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {webhookSubscribed ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Webhook Active
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4" />
                  Enable Webhook
                </>
              )}
            </button>

            {webhookSubscribed && (
              <button
                onClick={handleToggleRealtime}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  realtimeEnabled
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              >
                {realtimeEnabled ? (
                  <>
                    <Radio className="w-4 h-4 animate-pulse" />
                    Live Updates ON
                  </>
                ) : (
                  <>
                    <BellOff className="w-4 h-4" />
                    Live Updates OFF
                  </>
                )}
              </button>
            )}

            {sseConnected && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 font-medium">
                <Radio className="w-4 h-4 animate-pulse" />
                Connected
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-800">⚠️ {error}</span>
          <button
            onClick={clearError}
            className="text-red-600 hover:text-red-800 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-green-800">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800 font-bold text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Loading State */}
      {(loading || loadingItems) && (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-3" />
          <p className="text-gray-600">Loading content...</p>
        </div>
      )}

      {/* Breadcrumbs */}
      {selectedLibrary && breadcrumbs.length > 0 && !loadingItems && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <nav className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <Home className="w-4 h-4" />
              Root
            </button>
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={index}>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`font-medium transition-colors ${
                    index === breadcrumbs.length - 1
                      ? 'text-gray-900 cursor-default'
                      : 'text-blue-600 hover:text-blue-700'
                  }`}
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>
      )}

      {/* Items Section */}
      {selectedLibrary && !loading && !loadingItems && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Contents ({items.length} items)
            </h2>
            <button
              onClick={() => loadFolderContents(selectedLibrary, breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1]?.id : null)}
              disabled={loadingItems}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loadingItems ? 'animate-spin' : ''}`} />
              {loadingItems ? 'Refreshing...' : 'Refresh RAG Status'}
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Folder className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No items in this folder.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => item.folder && handleFolderClick(item.id)}
                  className={`border rounded-lg p-4 flex items-center gap-4 transition-all ${
                    !item.folder && filesInRAG[item.id]
                      ? 'border-green-300 bg-green-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  } ${item.folder ? 'cursor-pointer hover:border-blue-400' : ''}`}
                >
                  {/* Icon */}
                  <div className="relative flex-shrink-0">
                    {item.folder ? (
                      <Folder className="w-8 h-8 text-blue-500" />
                    ) : (
                      <>
                        {getFileIcon(item.file?.mimeType || '', item.name)}
                        {filesInRAG[item.id] && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                            <CheckCircle className="w-3 h-3 text-white" fill="currentColor" />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900 truncate">
                        {item.name}
                      </h3>
                      {!item.folder && filesInRAG[item.id] && (
                        <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          ✓ In RAG
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {item.folder
                        ? `${item.folder.childCount} items`
                        : `${formatFileSize(item.size || 0)} • Modified: ${formatDate(item.lastModifiedDateTime)}`}
                    </p>
                  </div>

                  {/* Actions */}
                  {!item.folder && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isRAGMode ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUploadToPinecone(
                              selectedLibrary,
                              item.id,
                              item.name,
                              item.file?.mimeType || 'application/pdf',
                              item.webUrl
                            );
                          }}
                          disabled={uploadingToPinecone === item.id || filesInRAG[item.id]}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                            filesInRAG[item.id]
                              ? 'bg-green-500 text-white cursor-not-allowed'
                              : uploadingToPinecone === item.id
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-orange-500 hover:bg-orange-600 text-white'
                          }`}
                        >
                          {uploadingToPinecone === item.id ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Uploading...
                            </>
                          ) : filesInRAG[item.id] ? (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Saved to RAG
                            </>
                          ) : (
                            <>
                              <Rocket className="w-4 h-4" />
                              Upload to Pinecone
                            </>
                          )}
                        </button>
                      ) : (
                        <>
                          {(item.name.toLowerCase().endsWith('.docx') || item.file?.mimeType?.includes('wordprocessingml')) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGenerateSlides(selectedLibrary, item.id, item.name);
                              }}
                              disabled={generatingSlidesItemId === item.id || downloadingItemId === item.id || uploadingItemId === item.id}
                              className="flex items-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                            >
                              {generatingSlidesItemId === item.id ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Presentation className="w-4 h-4" />
                                  Generate Slides
                                </>
                              )}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUploadClick(selectedLibrary, item.id, item.name);
                            }}
                            disabled={uploadingItemId === item.id || downloadingItemId === item.id}
                            className="flex items-center gap-2 px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                          >
                            {uploadingItemId === item.id ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload
                              </>
                            )}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(selectedLibrary, item.id, item.name);
                            }}
                            disabled={downloadingItemId === item.id || uploadingItemId === item.id}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                          >
                            {downloadingItemId === item.id ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4" />
                                Download
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!selectedLibrary && !loading && (
        <div className="text-center py-12 text-gray-500">
          <Folder className="w-16 h-16 mx-auto mb-3 text-gray-300" />
          <p>Select a SharePoint site and document library to browse content.</p>
        </div>
      )}

      {/* Upload Error Toast */}
      {uploadError && (
        <div className="fixed top-5 right-5 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md z-50 flex items-center gap-3">
          <span className="text-red-800">{uploadError}</span>
          <button
            onClick={() => setUploadError(null)}
            className="text-red-600 hover:text-red-800 font-bold text-xl ml-auto"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload Modal */}
      <UploadPdfModal
        isOpen={showUploadModal}
        uploadingFile={uploadingFile}
        setUploadingFile={setUploadingFile}
        isUploading={isUploading}
        onUpload={handleUploadToAnalysis}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default SharePointSync;
